// @flow

import { ThemeProvider, css } from '@emotion/react'
import styled from '@emotion/styled'
import { Link, graphql, useStaticQuery, withPrefix } from 'gatsby'
import * as React from 'react'

import { colors, media, themes } from '@toggl/style'
import {
  Button,
  Footer,
  Global,
  Header,
  Hire,
  Navigation,
  Plan,
  Track,
  useCloseOnBlurEffect,
  useDropdown,
} from '@toggl/ui'
import typeof HeaderAnnouncement from 'components/HeaderAnnouncement'

import { GlobalHeaderContents } from './header'
import { GlobalLoginTrigger } from './header/GlobalLogin'
import { LayoutRoot } from './LayoutRoot'

declare var process: WebProcess

type Props = {
  ...React.ElementProps<typeof LayoutRoot>,
  announcement?: React.Element<HeaderAnnouncement>,
  children?: React.Node,
  background?: string,
}

export const LayoutGlobal = ({
  children,
  announcement,
  background,
  ...rest
}: Props): React.Element<typeof LayoutRoot> => {
  const data = useStaticQuery(graphql`
    query layoutGlobalJobsQuery {
      allJobYaml(filter: { listed: { eq: true } }) {
        totalCount
      }
    }
  `)

  const openPositions = data.allJobYaml.totalCount

  return (
    <LayoutRoot {...rest}>
      <ThemeProvider theme={themes.global}>
        <Navigation.DropdownProvider>
          <GlobalHeader
            announcement={announcement}
            background={background}
            openPositions={openPositions}
          />
        </Navigation.DropdownProvider>
        {children}
        <Footer.Wrapper
          Link={Link}
          openPositions={openPositions}
          logo={<IconGlobal />}
          products={[
            <TrackLink to={'/track/'} key="track">
              <Track />
              <span>Effortless time tracking</span>
            </TrackLink>,
            <PlanLink href={withPrefix('/plan/')} key="plan">
              <Plan />
              <span>Beautifully simple work planning</span>
            </PlanLink>,
            <HireLink href={withPrefix('/hire/')} key="hire">
              <Hire />
              <span>Intelligent candidate-screening</span>
            </HireLink>,
          ]}
        />
      </ThemeProvider>
    </LayoutRoot>
  )
}

const GlobalHeader = ({ announcement, openPositions, background }) => {
  const headerRef = React.useRef(null)
  const { isOpen, close: closeHeader } = useDropdown()
  useCloseOnBlurEffect(headerRef, closeHeader)

  return (
    <Header ref={headerRef}>
      {announcement ? announcement : null}

      <Navigation.Header
        background={background}
        logoWidth={['75px', '85px']}
        logo={
          <Link to="/">
            <StyledLogo open={isOpen} />
          </Link>
        }
        secondaryLinks={<GlobalLoginTrigger />}
        cta={
          <Navigation.DropdownBaseTrigger item="global-products">
            <Button.Primary reducedDevicePadding>Try Toggl</Button.Primary>
          </Navigation.DropdownBaseTrigger>
        }
      >
        <GlobalHeaderContents openPositions={openPositions} />
      </Navigation.Header>
    </Header>
  )
}

const StyledLogo = styled(Global)`
  fill: ${colors.darkPurple};

  ${media.mq[1]} {
    fill: ${({ open }) => (open ? colors.darkPurple : colors.yellow)};
  }
`

const IconGlobal = () => (
  <div
    css={css`
      width: 85px;
      svg {
        width: 100%;
      }
    `}
  >
    <Global />
  </div>
)

const TrackLink = styled(Link)`
  &:hover {
    color: ${colors.pink};
  }
`
const PlanLink = styled.a`
  &:hover {
    color: ${colors.violet};
  }
`
const HireLink = styled.a`
  &:hover span {
    color: ${colors.orange};
  }
`
